import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Input, notification, Row, Col, Button, Breadcrumb, Typography, Spin } from 'antd';
import { backButton, formItemLayout, handleBackendFormErrors, tailFormItemLayout, unexpectedError } from '../../../common_functions';

const SheetDimension = props => {
  const { section, id } = useParams();
  const [dimension, setDimension] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        if (id !== 'new') {
          let { data } = await props.getItem('sheet_dimensions', id);
          setDimension(data);
          formRef.current.setFieldsValue(data);
        }
      } catch (error) {
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [section, id]);

  let onFinish = async values => {
    setLoading(true);
    try {
      let newState = { ...dimension, ...values };
      const response = await props.saveItem('sheet_dimensions', newState);
      if (response) {
        notification['success']({
          message: 'Negative sheet dimension saved successfully!',
          duration: 2,
        });
        backButton(props, `/settings/negative-sheet-dimensions`);
      }
    } catch (errorResponse) {
      handleBackendFormErrors(formRef, errorResponse);
    } finally {
      setLoading(false);
    }
  };

  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb separator="/" items={[{ title: "Settings", href: "/settings/negative-sheet-dimensions" }, { title: "Negative Sheet Dimensions", href: "/settings/negative-sheet-dimensions" }, { title: id != 'new' ? `Edit Negative Sheet Dimension` : 'Add Negative Sheet Dimension' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {id != 'new' ? `Edit Negative Sheet Dimension: ${id}` : 'Add Negative Sheet Dimension'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="label"
                    label="Label"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Label is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter label" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="aa_code"
                    label="AA Code"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'AA code is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter AA code" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="mm_height"
                    label="Size MM H"
                    rules={[
                      {
                        // whitespace: true,
                        required: true,
                        message: 'Size mm height is required!',
                      },
                      {
                        pattern: /^\d*\.?\d+$/,
                        message: 'Please enter a valid number',
                      },
                    ]}
                  >
                    <Input placeholder="Enter size mm height" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="mm_width"
                    label="Size MM W"
                    rules={[
                      {
                        // whitespace: true,
                        required: true,
                        message: 'Size mm width is required!',
                      },
                      {
                        pattern: /^\d*\.?\d+$/,
                        message: 'Please enter a valid number',
                      },
                    ]}
                  >
                    <Input placeholder="Enter size mm width" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="height"
                    label='Size " H'
                    rules={[
                      {
                        // whitespace: true,
                        required: true,
                        message: 'Size height is required!',
                      },
                      {
                        pattern: /^\d*\.?\d+$/,
                        message: 'Please enter a valid number',
                      },
                    ]}
                  >
                    <Input placeholder="Enter size height" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="width"
                    label='Size " W'
                    rules={[
                      {
                        // whitespace: true,
                        required: true,
                        message: 'Size width is required!',
                      },
                      {
                        pattern: /^\d*\.?\d+$/,
                        message: 'Please enter a valid number!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter size width" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="dim_classification"
                    label="Dimension Classification"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Dimension classification is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter dimension classification" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="obj_classification"
                    label="Object Classification"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Object classification is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter object classification" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, '/settings/negative-sheet-dimensions')}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton(
                  props.roleType,
                  props.tabs_permissions.settings_negative_sheet_dimensions,
                  !!dimension ? dimension.id : ''
                )}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default SheetDimension;
